import { DefaultTheme, LocalDataKeys } from '@libs/types';
import { getCoreOptions } from '../foundation/coreOptions';
import { getLocalDataSnapshot } from '../foundation/utils/localDataStore';

export type TSystemColorScheme = DefaultTheme.light | DefaultTheme.dark;

/** User-selectable theme preference; `system` follows the OS `prefers-color-scheme`. */
export type TThemePreference = TSystemColorScheme | 'system';

/** `<html>` attribute carrying the resolved scheme; package CSS keys `color-scheme` and the theme's semantic colors off it. */
export const COLOR_SCHEME_ATTRIBUTE = 'data-theme';

const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

const isColorScheme = (value: unknown): value is TSystemColorScheme => value === DefaultTheme.light || value === DefaultTheme.dark;

/** `disableDarkTheme` kill-switch from the package's `coreOptions` (or the stand's env override): forces the light scheme. */
export const isDarkThemeDisabled = () => !!getCoreOptions().theme?.disableDarkTheme;

/** OS-level color scheme from `prefers-color-scheme`; light when `matchMedia` is unavailable. */
export const getOsColorScheme = (): TSystemColorScheme =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(DARK_SCHEME_QUERY)?.matches
        ? DefaultTheme.dark
        : DefaultTheme.light;

/**
 * Resolves the effective scheme, highest priority first: a `forced` scheme (a package that supports only one),
 * the `disableDarkTheme` kill-switch, an explicit `light`/`dark` user preference (saved via
 * `save_local_data:theme`), otherwise (`system` or nothing saved) the OS scheme.
 */
export const resolveColorScheme = (preference: TThemePreference | null, osScheme: TSystemColorScheme, forced?: TSystemColorScheme): TSystemColorScheme => {
    if (forced) {
        return forced;
    }
    if (isDarkThemeDisabled()) {
        return DefaultTheme.light;
    }
    return isColorScheme(preference) ? preference : osScheme;
};

/**
 * Current scheme for code outside React. Reads the attribute `CoreProvider` keeps on `<html>`, so it agrees
 * with what is painted (including a forced scheme); before the provider mounts it resolves the scheme itself.
 */
export const getColorScheme = (): TSystemColorScheme => {
    const applied = typeof document !== 'undefined' ? document.documentElement.getAttribute(COLOR_SCHEME_ATTRIBUTE) : null;
    return isColorScheme(applied) ? applied : resolveColorScheme(getLocalDataSnapshot<TThemePreference>(LocalDataKeys.theme), getOsColorScheme());
};

/** Subscribes to OS scheme changes; returns the unsubscribe function. */
export const subscribeOsColorScheme = (onChange: (scheme: TSystemColorScheme) => void) => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => undefined;
    }
    const mediaQueryList = window.matchMedia(DARK_SCHEME_QUERY);
    const handler = (event: MediaQueryListEvent) => onChange(event.matches ? DefaultTheme.dark : DefaultTheme.light);
    mediaQueryList?.addEventListener?.('change', handler);
    return () => mediaQueryList?.removeEventListener?.('change', handler);
};
